import type { ReactNode, Ref } from "react";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";
import { laneIdProps } from "./boardAttributes";
import {
    getExpandedColumns,
    getLaneLabel,
    type BoardCollapse,
} from "./boardCollapse";
import { BoardCollapseToggle } from "./BoardCollapsedColumn";
import { BoardTruncatedText } from "./BoardTruncatedText";
import { BoardWindowedLanes, type BoardLaneRefs } from "./BoardWindowedLanes";
import { estimateLaneHeight } from "./boardWindowing";
import styles from "./Board.module.scss";
import laneStyles from "./BoardLanes.module.scss";

export interface BoardLayoutCell {
    column: BoardColumnDef;
    lane: BoardLaneDef | null;
    placement: BoardPlacement;
    /** Ids of the titles naming the cell's list. */
    labelledBy: string;
    /** Windowed, decided from the counts before any drag so it holds while dragging. */
    isWindowed: boolean;
}

interface BoardLaneHeaderProps {
    titleId: string;
    title: ReactNode;
    icon?: ReactNode;
    count: number;
    toggle: ReactNode;
    ref?: Ref<HTMLDivElement>;
}

/**
 * Lane header row. It sticks under the column headers while its lane is in
 * view; its content stays pinned to the board's left edge.
 */
const BoardLaneHeader = ({
    titleId,
    title,
    icon,
    count,
    toggle,
    ref,
}: Readonly<BoardLaneHeaderProps>) => (
    <div ref={ref} className={laneStyles.laneHeader}>
        <div className={laneStyles.laneHeaderContent}>
            {toggle}
            {Boolean(icon) && (
                <span aria-hidden className={styles.headerIcon}>
                    {icon}
                </span>
            )}
            <h3 id={titleId} className={styles.headerTitle}>
                <BoardTruncatedText>{title}</BoardTruncatedText>
                <span className={styles.headerCount}>{count}</span>
            </h3>
        </div>
    </div>
);

export interface BoardLaneWindowing {
    pinnedLaneIds: readonly string[];
}

interface BoardLaneRowsProps {
    columns: readonly BoardColumnDef[];
    lanes: readonly BoardLaneDef[];
    /** Cards per lane: every copy counts. */
    laneCounts: ReadonlyMap<string, number>;
    collapse: BoardCollapse;
    /** Cards in a cell before any drag. */
    getCellCount: (placement: BoardPlacement) => number;
    /** Set on large boards: only lanes, and cards in every cell, near the viewport mount. */
    laneWindowing?: BoardLaneWindowing;
    idPrefix: string;
    getColumnTitleId: (columnIndex: number) => string;
    renderHeader: (column: BoardColumnDef, columnIndex: number) => ReactNode;
    renderCell: (cell: BoardLayoutCell) => ReactNode;
    /** Collapsed column strips, spanning every lane under the header row. */
    strips: ReactNode;
    /** Measure the sticky rows, for the offsets under them. */
    headerRowRef?: Ref<HTMLDivElement>;
    /** Lane headers share a height: only the first mounted one gets it. */
    laneHeaderRef?: Ref<HTMLDivElement>;
}

/**
 * A sticky row of column headers over one block per lane. Each block is a
 * subgrid row, so lanes window as plain stacked blocks.
 */
export const BoardLaneRows = ({
    columns,
    lanes,
    laneCounts,
    collapse,
    getCellCount,
    laneWindowing,
    idPrefix,
    getColumnTitleId,
    renderHeader,
    renderCell,
    strips,
    headerRowRef,
    laneHeaderRef,
}: Readonly<BoardLaneRowsProps>) => {
    const { onToggleLane } = collapse;
    const expandedColumns = getExpandedColumns(columns, collapse);
    const estimateLaneSize = (laneIndex: number) => {
        const laneId = lanes[laneIndex].id;
        return estimateLaneHeight(
            collapse.laneIds.has(laneId)
                ? []
                : expandedColumns.map(({ column }) =>
                      getCellCount({ columnId: column.id, laneId }),
                  ),
        );
    };
    const renderLane = (
        lane: BoardLaneDef,
        laneIndex: number,
        { measureRef, headerRef }: BoardLaneRefs,
    ) => {
        const laneTitleId = `${idPrefix}lane${laneIndex}`;
        const isExpanded = !collapse.laneIds.has(lane.id);
        return (
            <div
                key={lane.id}
                ref={measureRef}
                data-index={laneIndex}
                {...laneIdProps(lane.id)}
                role="group"
                aria-labelledby={laneTitleId}
                className={laneStyles.lane}
            >
                <BoardLaneHeader
                    ref={headerRef}
                    titleId={laneTitleId}
                    title={lane.title}
                    icon={lane.icon}
                    count={laneCounts.get(lane.id) ?? 0}
                    toggle={
                        onToggleLane && (
                            <BoardCollapseToggle
                                name={getLaneLabel(lane)}
                                isExpanded={isExpanded}
                                onToggle={() => onToggleLane(lane.id)}
                            />
                        )
                    }
                />
                {isExpanded &&
                    expandedColumns.map(({ column, columnIndex }) => {
                        const placement = {
                            columnId: column.id,
                            laneId: lane.id,
                        };
                        return (
                            <div
                                key={column.id}
                                className={laneStyles.laneCell}
                                style={{ gridColumn: columnIndex + 1 }}
                            >
                                {renderCell({
                                    column,
                                    lane,
                                    placement,
                                    labelledBy: `${getColumnTitleId(columnIndex)} ${laneTitleId}`,
                                    isWindowed: laneWindowing !== undefined,
                                })}
                            </div>
                        );
                    })}
            </div>
        );
    };

    return (
        <>
            <div ref={headerRowRef} className={laneStyles.columnHeaderRow}>
                {columns.map((column, columnIndex) => (
                    <div
                        key={column.id}
                        style={{ gridColumn: columnIndex + 1 }}
                    >
                        {renderHeader(column, columnIndex)}
                    </div>
                ))}
            </div>
            {strips}
            {laneWindowing === undefined ? (
                <div className={laneStyles.lanes}>
                    {lanes.map((lane, laneIndex) =>
                        renderLane(lane, laneIndex, {
                            headerRef:
                                laneIndex === 0 ? laneHeaderRef : undefined,
                        }),
                    )}
                </div>
            ) : (
                <BoardWindowedLanes
                    lanes={lanes}
                    estimateLaneSize={estimateLaneSize}
                    pinnedLaneIds={laneWindowing.pinnedLaneIds}
                    firstLaneHeaderRef={laneHeaderRef}
                    renderLane={renderLane}
                />
            )}
        </>
    );
};
